import React, { useRef } from 'react';
import {
  TextField, Grid, Checkbox, FormControlLabel, MenuItem,
  FormControl, InputLabel, Select, Autocomplete, Box,
  RadioGroup, Radio, FormLabel, FormHelperText, Typography
} from '@mui/material';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import YearRangeDatePicker from '../../components/YearRangeDatePicker';

const attendantOptions = [
  'Friend', 'Aunt', 'Uncle', 'Wife', 'Husband', 'Son', 'Daughter', 'Mother',
  'Father', 'Brother', 'Sister', 'Grandmother', 'Grandfather', 'Cousin',
  'Nephew', 'Niece', 'Mother-in-law', 'Father-in-law', 'Sister-in-law',
  'Brother-in-law', 'Guardian', 'Caretaker', 'Neighbor', 'Colleague',
  'Partner', 'Others'
];

const allergyOptions = [
  'Peanuts', 'Tree nuts', 'Shellfish', 'Fish', 'Milk (Dairy)', 'Eggs',
  'Wheat (Gluten)', 'Soy', 'Pollen', 'Dust mites', 'Latex', 'Insect stings',
  'Penicillin', 'Other medication allergies'
];

const emailDomains = ['@gmail.com', '@yahoo.com', '@outlook.com', '@hotmail.com', '@icloud.com'];

/**
 * Shared Patient form fields used by both Add and Edit pages.
 * All state and handlers come from the parent via props.
 */
const PatientForm = ({
  formData,
  errors,
  handleChange,
  handleBlur,
  handlePhoneChange,
  handleEmailChange,
  handleAllergyChange,
  handleAttendantRelationChange,
  handleOnlinePaymentChange,
  handleCardTypeChange,
}) => {
  const allergyInputRef = useRef(null);

  const getEmailSuggestions = (input) => {
    const str = String(input || '');
    if (!str || str.includes('@')) return [];
    return emailDomains.map(d => str + d);
  };

  return (
    <Grid container spacing={2}>
      {/* Name */}
      <Grid item xs={12} sm={6}>
        <TextField
          label={<span>Name <span style={{ color: 'red' }}>*</span></span>}
          name="name"
          value={formData.name}
          onChange={handleChange}
          onBlur={handleBlur}
          fullWidth
          error={!!errors.name}
          helperText={errors.name || ' '}
        />
      </Grid>

      {/* Title */}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth error={!!errors.title}>
          <InputLabel><span>Title <span style={{ color: 'red' }}>*</span></span></InputLabel>
          <Select name="title" value={formData.title} onChange={handleChange} onBlur={handleBlur} label="Title *">
            <MenuItem value="Mr.">Mr</MenuItem>
            <MenuItem value="Mrs.">Mrs</MenuItem>
            <MenuItem value="Ms.">Ms</MenuItem>
            <MenuItem value="Dr.">Dr</MenuItem>
          </Select>
          <Typography variant="caption" color="error">{errors.title || ' '}</Typography>
        </FormControl>
      </Grid>

      {/* Email */}
      <Grid item xs={12} sm={6}>
        <Typography variant="body2" sx={{ mb: 1 }}>Email <span style={{ color: 'red' }}>*</span></Typography>
        <Autocomplete
          freeSolo
          options={getEmailSuggestions(formData.email)}
          inputValue={String(formData.email || '')}
          onInputChange={(_, newVal) => handleEmailChange(newVal || '')}
          onChange={(_, newVal) => handleEmailChange(newVal || '')}
          renderInput={(params) => (
            <TextField
              {...params}
              name="email"
              fullWidth
              error={!!errors.email}
              helperText={errors.email || ' '}
              onBlur={handleBlur}
            />
          )}
        />
      </Grid>

      {/* Mobile */}
      <Grid item xs={12} sm={6}>
        <Typography variant="body2" sx={{ mb: 1 }}>Mobile <span style={{ color: 'red' }}>*</span></Typography>
        <PhoneInput
          country="in"
          value={formData.mobile}
          onChange={(value) => handlePhoneChange('mobile', value)}
          enableSearch
          inputProps={{ name: 'mobile' }}
          containerStyle={{ width: '100%' }}
          inputStyle={{ width: '100%', height: '56px' }}
          buttonStyle={{ height: '56px' }}
          onBlur={() => handleBlur({ target: { name: 'mobile', value: formData.mobile } })}
        />
        <div style={{ color: errors.mobile ? '#d32f2f' : 'rgba(0,0,0,0.6)', fontSize: '0.75rem', marginTop: '4px', marginLeft: '14px' }}>
          {errors.mobile || ' '}
        </div>
      </Grid>

      {/* Date of Birth */}
      <Grid item xs={12} sm={6}>
        <YearRangeDatePicker
          label={<span>Date of Birth <span style={{ color: 'red' }}>*</span></span>}
          name="date_of_birth"
          value={formData.date_of_birth}
          onChange={handleChange}
          error={!!errors.date_of_birth}
          helperText={errors.date_of_birth || ' '}
          onBlur={handleBlur}
        />
      </Grid>

      {/* Age (auto-filled) */}
      <Grid item xs={12} sm={6}>
        <TextField
          label={<span>Age <span style={{ color: 'red' }}>*</span></span>}
          name="age"
          type="number"
          value={formData.age}
          InputProps={{ readOnly: true }}
          fullWidth
          error={!!errors.age}
          helperText={errors.age || 'Auto-calculated from DOB'}
        />
      </Grid>

      {/* Sex */}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth error={!!errors.sex}>
          <InputLabel><span>Sex <span style={{ color: 'red' }}>*</span></span></InputLabel>
          <Select name="sex" value={formData.sex} onChange={handleChange} onBlur={handleBlur} label="Sex *">
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </Select>
          <Typography variant="caption" color="error">{errors.sex || ' '}</Typography>
        </FormControl>
      </Grid>

      {/* Address */}
      <Grid item xs={12}>
        <TextField
          label={<span>Address <span style={{ color: 'red' }}>*</span></span>}
          name="address"
          value={formData.address}
          onChange={handleChange}
          onBlur={handleBlur}
          fullWidth
          error={!!errors.address}
          helperText={errors.address || ' '}
        />
      </Grid>

      {/* Attendant Name */}
      <Grid item xs={12} sm={6}>
        <Typography variant="body2" sx={{ mb: 1 }}>Attendant Name <span style={{ color: 'red' }}>*</span></Typography>
        <TextField
          name="attendantName"
          value={formData.attendantName}
          onChange={handleChange}
          onBlur={handleBlur}
          fullWidth
          error={!!errors.attendantName}
          helperText={errors.attendantName || ' '}
        />
      </Grid>

      {/* Attendant Phone */}
      <Grid item xs={12} sm={6}>
        <Typography variant="body2" sx={{ mb: 1 }}>Attendant Phone <span style={{ color: 'red' }}>*</span></Typography>
        <PhoneInput
          country="in"
          value={formData.attendantPhone}
          onChange={(value) => handlePhoneChange('attendantPhone', value)}
          enableSearch
          inputProps={{ name: 'attendantPhone' }}
          containerStyle={{ width: '100%' }}
          inputStyle={{ width: '100%', height: '56px', borderColor: errors.attendantPhone ? '#d32f2f' : undefined }}
          buttonStyle={{ height: '56px' }}
          onBlur={() => handleBlur({ target: { name: 'attendantPhone', value: formData.attendantPhone } })}
        />
        <div style={{ color: errors.attendantPhone ? '#d32f2f' : 'rgba(0,0,0,0.6)', fontSize: '0.75rem', marginTop: '4px', marginLeft: '14px' }}>
          {errors.attendantPhone || ' '}
        </div>
      </Grid>

      {/* Attendant Relation */}
      <Grid item xs={12} sm={6}>
        <Autocomplete
          freeSolo
          options={attendantOptions}
          value={formData.attendantRelation}
          onChange={(_, newVal) => handleAttendantRelationChange(newVal)}
          onInputChange={(_, newVal, reason) => { if (reason === 'input') handleAttendantRelationChange(newVal); }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={<span>Attendant Relation <span style={{ color: 'red' }}>*</span></span>}
              fullWidth
              error={!!errors.attendantRelation}
              helperText={errors.attendantRelation || ' '}
              onBlur={handleBlur}
            />
          )}
        />
      </Grid>

      {/* Payment Method */}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth error={!!errors.payment_method}>
          <InputLabel><span>Payment Method <span style={{ color: 'red' }}>*</span></span></InputLabel>
          <Select name="payment_method" value={formData.payment_method} onChange={handleChange} onBlur={handleBlur} label="Payment Method *">
            <MenuItem value="Cash">Cash</MenuItem>
            <MenuItem value="Insurance">Insurance</MenuItem>
            <MenuItem value="Online">Online</MenuItem>
          </Select>
          <FormHelperText>{errors.payment_method || ' '}</FormHelperText>
        </FormControl>
      </Grid>

      {/* Online Payment Options */}
      {formData.payment_method === 'Online' && (
        <Grid item xs={12}>
          <Box sx={{ mt: 1 }}>
            <FormLabel sx={{ mb: 1 }}>Select Online Payment Method</FormLabel>
            <RadioGroup row value={formData.online_payment_option} onChange={(e) => handleOnlinePaymentChange(e.target.value)}>
              <FormControlLabel value="upi" control={<Radio />} label="UPI" />
              <FormControlLabel value="card" control={<Radio />} label="Card" />
              <FormControlLabel value="netbanking" control={<Radio />} label="Net Banking" />
            </RadioGroup>
            {errors.online_payment_option && <Typography variant="caption" color="error">{errors.online_payment_option}</Typography>}
            {formData.online_payment_option === 'card' && (
              <Box sx={{ mt: 1 }}>
                <FormLabel>Card Type</FormLabel>
                <RadioGroup row value={formData.card_type} onChange={(e) => handleCardTypeChange(e.target.value)}>
                  <FormControlLabel value="Debit" control={<Radio />} label="Debit Card" />
                  <FormControlLabel value="Credit" control={<Radio />} label="Credit Card" />
                </RadioGroup>
                {errors.card_type && <Typography variant="caption" color="error">{errors.card_type}</Typography>}
              </Box>
            )}
          </Box>
        </Grid>
      )}

      {/* Insurance */}
      <Grid item xs={12} sm={6}>
        <FormControlLabel
          control={<Checkbox checked={formData.insuranceAvailable} onChange={handleChange} name="insuranceAvailable" />}
          label="Insurance Available"
        />
      </Grid>

      {/* Any Allergies */}
      <Grid item xs={12} sm={6}>
        <FormControlLabel
          control={<Checkbox checked={formData.anyAllergies} onChange={handleChange} name="anyAllergies" />}
          label="Any Allergies"
        />
      </Grid>

      {/* Mention Allergies */}
      {formData.anyAllergies && (
        <Grid item xs={12}>
          <Autocomplete
            multiple
            freeSolo
            options={allergyOptions}
            value={formData.mentionAllergies}
            onChange={(_, newVal) => handleAllergyChange(newVal)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Mention Allergies"
                inputRef={allergyInputRef}
                onBlur={() => {
                  const typed = allergyInputRef.current?.value?.trim();
                  if (typed && !formData.mentionAllergies.includes(typed)) {
                    handleAllergyChange([...formData.mentionAllergies, typed]);
                  }
                }}
                fullWidth
              />
            )}
          />
        </Grid>
      )}

      {/* UHID No (always read-only) */}
      <Grid item xs={12}>
        <TextField
          label="UHID No"
          name="uhidNo"
          value={formData.uhidNo}
          InputProps={{ readOnly: true }}
          fullWidth
        />
      </Grid>
    </Grid>
  );
};

export default PatientForm;
